import { GetIssueResponseDtoSeverityEnum } from '@issuemanager/api-client';
import { Chip } from '@issuemanager/apps/client/components';
import {
  useAtlasCoreThemeTokens,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import { useSeverityLocalizer } from '@issuemanager/client/features/issues';
import { type SxProps } from '@mui/material';
import { useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

type SeverityChipProps = {
  severity: GetIssueResponseDtoSeverityEnum | null;
};

export function IssueSeverityChip({
  severity,
}: SeverityChipProps): JSX.Element | null {
  const semantic = useAtlasSemanticThemeTokens();

  const issueSeverities = useIssueSeverities();
  const issueSeverity = issueSeverities.find(
    (issueStatus) => issueStatus.value === severity,
  );

  const commonStyle: SxProps = {
    fontWeight: semantic.fontWeight.emphasis.value,
    backgroundColor: issueSeverity?.backgroundColor,
    color: issueSeverity?.color,
    borderRadius: 1,
    padding: semantic.spacing.default.value,
    letterSpacing: '1px',
  };

  if (severity === undefined) {
    return null;
  }

  return (
    <Chip
      data-testid='severity-chip'
      sx={commonStyle}
      label={issueSeverity?.label}
      title={severity ?? 'null'}
    />
  );
}

export interface IssueSeverityData {
  id: string;
  value: GetIssueResponseDtoSeverityEnum | null;
  label: string;
  color: string;
  backgroundColor: string;
}

export interface IssueSeverityDataWithoutNullValue
  extends Omit<IssueSeverityData, 'value'> {
  value: GetIssueResponseDtoSeverityEnum;
}

export function useIssueSeverities(includeNotSet = true): IssueSeverityData[] {
  const { t } = useTranslation('issue');
  const getSeverityLabel = useSeverityLocalizer();
  const semantic = useAtlasSemanticThemeTokens();
  const core = useAtlasCoreThemeTokens();

  const whiteColor = core.color.legacy.white.value;
  const darkColor = semantic.color.text.body.value;

  const issueStatuses: IssueSeverityData[] = useMemo(
    () => [
      {
        id: '1',
        value: GetIssueResponseDtoSeverityEnum.Low,
        label:
          getSeverityLabel(GetIssueResponseDtoSeverityEnum.Low) ??
          GetIssueResponseDtoSeverityEnum.Low,
        color: darkColor,
        backgroundColor: getSeverityColor(
          GetIssueResponseDtoSeverityEnum.Low,
          semantic,
        ),
      },
      {
        id: '2',
        value: GetIssueResponseDtoSeverityEnum.Medium,
        label:
          getSeverityLabel(GetIssueResponseDtoSeverityEnum.Medium) ??
          GetIssueResponseDtoSeverityEnum.Medium,
        color: darkColor,
        backgroundColor: getSeverityColor(
          GetIssueResponseDtoSeverityEnum.Medium,
          semantic,
        ),
      },
      {
        id: '3',
        value: GetIssueResponseDtoSeverityEnum.High,
        label:
          getSeverityLabel(GetIssueResponseDtoSeverityEnum.High) ??
          GetIssueResponseDtoSeverityEnum.High,
        color: darkColor,
        backgroundColor: getSeverityColor(
          GetIssueResponseDtoSeverityEnum.High,
          semantic,
        ),
      },
      {
        id: '4',
        value: GetIssueResponseDtoSeverityEnum.Critical,
        label:
          getSeverityLabel(GetIssueResponseDtoSeverityEnum.Critical) ??
          GetIssueResponseDtoSeverityEnum.Critical,
        color: whiteColor,
        backgroundColor: getSeverityColor(
          GetIssueResponseDtoSeverityEnum.Critical,
          semantic,
        ),
      },
      ...(includeNotSet
        ? [
            {
              id: '5',
              value: null,
              label: t('statistics.severity.notSet'),
              color: darkColor,
              backgroundColor: getSeverityColor(null, semantic),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getSeverityLabel],
  );

  return issueStatuses;
}

function getSeverityColor(
  severity: GetIssueResponseDtoSeverityEnum | null,
  semantic: ReturnType<typeof useAtlasSemanticThemeTokens>,
): string {
  switch (severity) {
    case GetIssueResponseDtoSeverityEnum.Critical:
      return '#ED8584';
    case GetIssueResponseDtoSeverityEnum.High:
      return semantic.color.accent['03'].value;
    case GetIssueResponseDtoSeverityEnum.Medium:
      return semantic.color.accent['04'].value;
    case GetIssueResponseDtoSeverityEnum.Low:
      return semantic.color.accent['02'].value;
    case null:
      return semantic.color.action.disabled.value;
    default:
      return semantic.color.action.disabled.value;
  }
}
